import {trapFocus, removeTrapFocus} from '@theme/focus';
import { getCustomerAppBaseUrl } from "@theme/gadget-envs";

/** US state full name -> 2-digit code for converting Google Address Validation data */
const US_STATE_NAME_TO_CODE = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", "District of Columbia": "DC",
  Florida: "FL", Georgia: "GA", Hawaii: "HI", Idaho: "ID", Illinois: "IL",
  Indiana: "IN", Iowa: "IA", Kansas: "KS", Kentucky: "KY", Louisiana: "LA",
  Maine: "ME", Maryland: "MD", Massachusetts: "MA", Michigan: "MI", Minnesota: "MN",
  Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE", Nevada: "NV",
  "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
  "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH", Oklahoma: "OK",
  Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT", Vermont: "VT",
  Virginia: "VA", Washington: "WA", "West Virginia": "WV", Wisconsin: "WI", Wyoming: "WY",
};

class AddressBookList {
  constructor() {
    this.section = document.querySelector('.section--address-book');
    this.container = document.querySelector('.rs-address-book-list');
    this.searchInput = document.querySelector('.rs-address-book--search .rs-input');
    this.addAddressButtons = document.querySelectorAll('.rs-add-address');
    this.addAddressModal = document.querySelector('.rs-address-book-address-modal');
    this.modalMask = this.addAddressModal?.querySelector('.rs-modal-mask');
    this.closeAddressModalButtons = document.querySelectorAll('.rs-modal-close');
    this.addressForm = document.querySelector('.rs-address-book-address-form');
    this.addressFormCompanyNameInput = this.addressForm?.querySelector('#input-address-company-name');
    this.addressFormAttentionInput = this.addressForm?.querySelector('#input-address-attention');
    this.addressFormCountryInput = this.addressForm?.querySelector('.rs-custom-select-address-country-input');
    this.addressFormProvinceInput = this.addressForm?.querySelector('.rs-custom-select-address-state-input');
    this.addressFormCityInput = this.addressForm?.querySelector('#input-address-city');
    this.addressFormAddress1Input = this.addressForm?.querySelector('#input-address-line-1');
    this.addressFormAddress2Input = this.addressForm?.querySelector('#input-address-line-2');
    this.addressFormZipInput = this.addressForm?.querySelector('#input-address-zip');
    this.submitAddressFormButtonAdd = document.querySelector('.rs-address-book-address-form__add');
    this.submitAddressFormButtonEdit = document.querySelector('.rs-address-book-address-form__save');
    this.addressFormPhoneInput = this.addressForm?.querySelector('#input-address-phone');
    this.deleteAddressModal = document.querySelector('.rs-address-book-delete-modal');
    this.deleteModalMask = this.deleteAddressModal?.querySelector('.rs-modal-mask');
    this.closeDeleteModalButtons = document.querySelectorAll('.rs-modal-close-delete');
    this.deleteModalConfirmButton = document.querySelector('.rs-address-book-delete-modal__confirm');
    this.deleteModalCompanyName = document.querySelector('.rs-address-book-delete-modal__company-name');
    this.deleteModalAddressLine = document.querySelector('.rs-address-book-delete-modal__address-line');
    this.deleteModalPhone = document.querySelector('.rs-address-book-delete-modal__phone');
    this.allAddresses = [];
    this.filteredAddresses = [];
    this.currentEditingAddressId = null;
    this.currentDeletingAddressId = null;
    this.previousActiveElement = null; // Store element that had focus before modal opened
    /** @type {Record<string, Array<{label: string, value: string}>>|null} Province lists keyed by ISO from Shopify `all_country_option_tags`. */
    this._addressBookStatesByIso = null;
    this.mainContent = document.querySelector('main');
    /** @type {HTMLElement | null} Modal currently using the document-level focus trap */
    this._focusTrapModal = null;
    /** @type {HTMLElement[]} */
    this._inertedChrome = [];
    this._modalKeydownListening = false;
    this._boundModalKeydown = this.handleModalKeydown.bind(this);

    if (this.container) {
      this.init();
    }
  }

  async init() {
    // Wait for JWT and env to be available
    await this.waitForDependencies();
    const customerId = document.body.getAttribute('data-customer-id');
    if (!customerId) {
      console.error('Customer ID not found');
      return;
    }

    await this.fetchAndRenderAddresses(customerId);
    this.setupSearch();
    this.setupAddAddressButton();
    this.closeAddAddressModal();
    this.initModalSubmitButtons();
    this.setupDeleteModal();
    this.initInputValidation();
    this.bindStateChangeZipRevalidation();
    this.buildAddressBookStatesFromShopify();
    this.bindAddressBookCountryStateSync();
  }

  /**
   * @param {HTMLElement | null | undefined} modal
   * @returns {HTMLElement | null}
   */
  getModalFocusContainer(modal) {
    if (!(modal instanceof HTMLElement)) return null;
    const content = modal.querySelector('.rs-modal__content');
    return content instanceof HTMLElement ? content : modal;
  }

  /**
   * Trap keyboard focus inside an open modal dialog panel.
   * @param {HTMLElement} modal
   * @param {{ initialFocus?: HTMLElement | null }} [options]
   */
  activateModalFocusTrap(modal, options = {}) {
    if (!(modal instanceof HTMLElement)) return;

    const container = this.getModalFocusContainer(modal);
    if (!container) return;

    this._focusTrapModal = modal;

    if (this.mainContent instanceof HTMLElement) {
      this.mainContent.setAttribute('aria-hidden', 'true');
    }
    modal.setAttribute('aria-hidden', 'false');

    if (!this._modalKeydownListening) {
      document.addEventListener('keydown', this._boundModalKeydown);
      this._modalKeydownListening = true;
    }

    this.setPageInertBehindModal(modal);
    this.scheduleModalFocusTrap(modal, options);
  }

  /**
   * Prevent keyboard interaction with page chrome outside the open modal.
   * @param {HTMLElement} modal
   */
  setPageInertBehindModal(modal) {
    this.clearPageInertBehindModal();

    /** @type {HTMLElement[]} */
    this._inertedChrome = [];
    for (const node of document.body.children) {
      if (!(node instanceof HTMLElement)) continue;
      if (node === modal || node.contains(modal)) continue;
      node.setAttribute('inert', '');
      this._inertedChrome.push(node);
    }
  }

  clearPageInertBehindModal() {
    this._inertedChrome?.forEach((node) => node.removeAttribute('inert'));
    this._inertedChrome = [];
  }

  /**
   * Apply focus trap after layout settles (avoids empty focusable lists on first paint).
   * @param {HTMLElement} modal
   * @param {{ initialFocus?: HTMLElement | null }} [options]
   */
  scheduleModalFocusTrap(modal, options = {}) {
    const container = this.getModalFocusContainer(modal);
    if (!container) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this._focusTrapModal !== modal) return;

        trapFocus(container, {
          initialFocus:
            options.initialFocus instanceof HTMLElement ? options.initialFocus : undefined,
        });
      });
    });
  }

  /** Remove focus trap and restore background page to the tab order. */
  releaseModalFocusTrap() {
    removeTrapFocus();

    if (this._modalKeydownListening) {
      document.removeEventListener('keydown', this._boundModalKeydown);
      this._modalKeydownListening = false;
    }

    if (this.mainContent instanceof HTMLElement) {
      this.mainContent.setAttribute('aria-hidden', 'false');
    }

    this.clearPageInertBehindModal();
    this._focusTrapModal = null;
  }

  /**
   * Close the topmost open modal on Escape.
   * @param {KeyboardEvent} event
   */
  handleModalKeydown(event) {
    if (event.key !== 'Escape') return;

    const validationModal = document.getElementById('rs-address-validation-modal');
    if (validationModal) {
      event.preventDefault();
      validationModal.remove();
      document.body.style.overflow = '';
      removeTrapFocus();
      if (this.addAddressModal && !this.addAddressModal.classList.contains('rs-hidden')) {
        this.setPageInertBehindModal(this.addAddressModal);
        this.scheduleModalFocusTrap(this.addAddressModal, {
          initialFocus:
            this.addressFormCompanyNameInput instanceof HTMLElement
              ? this.addressFormCompanyNameInput
              : null,
        });
      } else {
        this.clearPageInertBehindModal();
      }
      return;
    }

    if (this.deleteAddressModal && !this.deleteAddressModal.classList.contains('rs-hidden')) {
      event.preventDefault();
      this.closeDeleteModal();
      return;
    }

    if (this.addAddressModal && !this.addAddressModal.classList.contains('rs-hidden')) {
      event.preventDefault();
      this.closeAddAddressModalHandler();
    }
  }

  closeAddAddressModal() {
    if (!this.addAddressModal) return;
    this.closeAddressModalButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.closeAddAddressModalHandler();
      });
    });
  }

  closeAddAddressModalHandler() {
    this.releaseModalFocusTrap();

    this.addAddressModal?.classList.add('rs-hidden');
    this.modalMask?.classList.add('rs-hidden');
    this.addAddressModal?.setAttribute('aria-hidden', 'true');
    this.modalMask?.setAttribute('aria-hidden', 'true');
    this.resetAddressForm();
    this.clearAllValidationMessages();
    this.errorValidation([this.addressFormCompanyNameInput, this.addressFormAddress1Input, this.addressFormCityInput, this.addressFormProvinceInput, this.addressFormZipInput, this.addressFormPhoneInput, this.addressFormCountryInput], 'close');

    // Return focus to the element that opened the modal
    if (this.previousActiveElement && this.previousActiveElement instanceof HTMLElement) {
      this.previousActiveElement.focus();
      this.previousActiveElement = null;
    }
  }

  initModalSubmitButtons() {
    this.submitAddressFormButtonAdd?.addEventListener('click', () => {
      this.addAddress();
    });

    this.submitAddressFormButtonEdit?.addEventListener('click', () => {
      this.editAddress();
    });
  }

  /**
   * Validate ZIP code format. US: 5 digits or 5+4. CA: A1A 1A1. IT: 5 digits (CAP). MX: 5 digits.
   */
  isValidZip(zip, countryCode) {
    if (!zip) return false;
    const code = (countryCode || 'US').toUpperCase();
    if (code === 'US') return /^\d{5}(-\d{4})?$/.test(zip);
    if (code === 'CA') return /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(zip.trim());
    if (code === 'IT') return /^\d{5}$/.test(zip.trim());
    if (code === 'MX') return /^\d{5}$/.test(zip.trim());
    return true;
  }

  /**
   * Check if a US ZIP code prefix matches the selected state.
   * ZIP prefix → state mapping based on USPS assignments.
   * @returns {boolean} true if matching or non-US, false if mismatch
   */
  isZipMatchingState(zip, stateCode, countryCode) {
    if ((countryCode || 'US').toUpperCase() !== 'US') return true;
    if (!zip || !stateCode) return true;
    const prefix = parseInt(zip.substring(0, 3), 10);
    if (isNaN(prefix)) return true;
    const zipStateMap = {
      AL: [[350, 369]], AK: [[995, 999]], AZ: [[850, 865]], AR: [[716, 729]],
      CA: [[900, 961]], CO: [[800, 816]], CT: [[60, 69]], DE: [[197, 199]],
      FL: [[320, 349]], GA: [[300, 319], [398, 399]], HI: [[967, 968]],
      ID: [[832, 838]], IL: [[600, 629]], IN: [[460, 479]], IA: [[500, 528]],
      KS: [[660, 679]], KY: [[400, 427]], LA: [[700, 714]], ME: [[39, 49]],
      MD: [[206, 219]], MA: [[10, 27], [55, 55]], MI: [[480, 499]],
      MN: [[550, 567]], MS: [[386, 397]], MO: [[630, 658]], MT: [[590, 599]],
      NE: [[680, 693]], NV: [[889, 898]], NH: [[30, 38]], NJ: [[70, 89]],
      NM: [[870, 884]], NY: [[100, 149], [90, 98]], NC: [[270, 289]],
      ND: [[580, 588]], OH: [[430, 459]], OK: [[730, 749]], OR: [[970, 979]],
      PA: [[150, 196]], RI: [[28, 29]], SC: [[290, 299]], SD: [[570, 577]],
      TN: [[370, 385]], TX: [[750, 799], [885, 885]], UT: [[840, 847]],
      VT: [[50, 59]], VA: [[220, 246]], WA: [[980, 994]], WV: [[247, 268]],
      WI: [[530, 549]], WY: [[820, 831]], DC: [[200, 205]],
      PR: [[6, 9], [900, 988]], VI: [[8, 8]], GU: [[969, 969]],
    };
    const ranges = zipStateMap[stateCode.toUpperCase()];
    if (!ranges) return true;
    return ranges.some(([min, max]) => prefix >= min && prefix <= max);
  }

  /**
   * Validate phone: at least 10 digits (11 if leading 1 for US/CA).
   */
  isValidPhone(phone) {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  /**
   * Parse Shopify `all_country_option_tags` (hidden select) and map provinces to each country ISO
   * in the address modal (matches country menu option `data-value`).
   */
  buildAddressBookStatesFromShopify() {
    const hidden = document.getElementById('rs-address-book-country-provinces-data');
    const countryMenu = document.querySelector('#rs-custom-select-address-country .rs-custom-select__menu');
    if (!hidden || !countryMenu) {
      this._addressBookStatesByIso = {};
      return;
    }

    const provincesByCountryName = {};
    for (const opt of hidden.options) {
      try {
        const raw = opt.getAttribute('data-provinces');
        if (!raw) continue;
        const arr = JSON.parse(raw);
        if (!Array.isArray(arr)) continue;
        provincesByCountryName[opt.value] = arr.map((pair) => ({
          label: pair[0] ?? '',
          value: pair[1] ?? pair[0] ?? '',
        }));
      } catch (_) {
        // ignore
      }
    }

    const normalize = (s) =>
      String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const findProvinces = (displayLabel) => {
      const dl = displayLabel?.trim() || '';
      if (dl && provincesByCountryName[dl]?.length) {
        return provincesByCountryName[dl];
      }
      const nTarget = normalize(dl);
      if (!nTarget) return [];
      for (const [name, provinces] of Object.entries(provincesByCountryName)) {
        if (provinces?.length && normalize(name) === nTarget) {
          return provinces;
        }
      }
      let best = null;
      let bestKeyLen = 0;
      for (const [name, provinces] of Object.entries(provincesByCountryName)) {
        if (!provinces?.length) continue;
        const nk = normalize(name);
        if (!nk) continue;
        const shorter = nk.length <= nTarget.length ? nk : nTarget;
        const longer = nk.length > nTarget.length ? nk : nTarget;
        if (shorter.length < 5) continue;
        if (longer.includes(shorter) && nk.length >= bestKeyLen) {
          best = provinces;
          bestKeyLen = nk.length;
        }
      }
      return best || [];
    };

    const states = {};
    countryMenu.querySelectorAll('.rs-custom-select__option').forEach((isoOpt) => {
      const iso = isoOpt.getAttribute('data-value')?.trim();
      if (!iso) return;
      const displayLabel =
        isoOpt.querySelector('.rs-custom-select__option-label')?.textContent?.trim() ||
        isoOpt.textContent?.trim() ||
        '';
      let list = findProvinces(displayLabel);
      if (iso === 'US') {
        list = list.map(({ label, value: rawValue }) => {
          const v =
            String(rawValue).length > 2
              ? US_STATE_NAME_TO_CODE[rawValue] || rawValue
              : rawValue;
          return { label, value: v };
        });
      }
      states[iso] = list;
    });
    this._addressBookStatesByIso = states;
  }

  addressBookCountryHasStateOptions(countryCode) {
    const code = (countryCode || '').trim().toUpperCase();
    const list = this._addressBookStatesByIso?.[code];
    return Array.isArray(list) && list.length > 0;
  }

  /**
   * Replace state/province menu options for the selected country ISO.
   */
  syncAddressBookStateMenu(iso, { selectedValue } = {}) {
    const stateWrapper = document.querySelector('#rs-custom-select-address-state');
    const menu = stateWrapper?.querySelector('.rs-custom-select__menu');
    const hiddenInput = stateWrapper?.querySelector('input[type="hidden"]');
    const valueDisplay = stateWrapper?.querySelector('.rs-custom-select__value');
    if (!menu || !hiddenInput || !valueDisplay) return;

    const code = (iso || 'US').trim().toUpperCase();
    if (!this._addressBookStatesByIso) {
      this.buildAddressBookStatesFromShopify();
    }
    const list =
      this._addressBookStatesByIso && this._addressBookStatesByIso[code] !== undefined
        ? this._addressBookStatesByIso[code]
        : [];

    const esc = (s) => this.escapeHtml(String(s ?? ''));
    menu.innerHTML = list
      .map(
        ({ label, value }) =>
          `<li class="rs-custom-select__option" role="option" tabindex="-1" data-value="${esc(value)}" aria-selected="false" aria-label="${esc(label)}"><span class="rs-custom-select__option-label">${esc(label)}</span></li>`,
      )
      .join('');

    // If a selected value was provided, set it immediately after building the menu
    if (selectedValue) {
      const normalizedValue = this.normalizeProvinceCode(selectedValue, code);
      const selectedOption =
        menu.querySelector(`[data-value="${selectedValue}"]`) ||
        menu.querySelector(`[data-value="${normalizedValue}"]`);
      if (selectedOption) {
        selectedOption.click();
        this.addressFormProvinceInput?.closest('.rs-custom-select__wrapper')?.classList.remove('rs-hidden');
        return;
      }
    }

    hiddenInput.value = '';
    valueDisplay.textContent = 'Select...';
    valueDisplay.classList.add('rs-custom-select__value--placeholder');

    this.addressFormProvinceInput?.closest('.rs-custom-select__wrapper')?.classList.remove('rs-hidden');
  }

  bindAddressBookCountryStateSync() {
    const countryHidden = this.addressFormCountryInput;
    if (!countryHidden || countryHidden.dataset.abCountryStateBound === '1') return;
    countryHidden.dataset.abCountryStateBound = '1';
    const onChange = () => {
      const iso = countryHidden.value?.trim() || 'US';
      if (!this._addressBookStatesByIso) {
        this.buildAddressBookStatesFromShopify();
      }
      this.syncAddressBookStateMenu(iso);
    };
    countryHidden.addEventListener('change', onChange);
    onChange();
  }

  /**
   * Collect address form values into a consistent shape used by add, edit, and validate flows.
   * @returns {{ companyName: string, attention: string, city: string, address1: string, address2: string, zip: string, phone: string, countryCode: string, provinceCode: string }}
   */
  getAddressFormValues() {
    const companyName = this.addressFormCompanyNameInput?.value.trim() || '';
    const attention = this.addressFormAttentionInput?.value.trim() || '';
    const city = this.addressFormCityInput?.value.trim() || '';
    const address1 = this.addressFormAddress1Input?.value.trim() || '';
    const address2 = this.addressFormAddress2Input?.value.trim() || '';
    const zip = this.addressFormZipInput?.value.trim() || '';
    const phone = this.addressFormPhoneInput?.value.trim() || '';

    const countrySelectWrapper = document.querySelector('#rs-custom-select-address-country');
    const countryHiddenInput = countrySelectWrapper?.querySelector('input[type="hidden"]');
    const countryCode = countryHiddenInput?.value || '';

    const stateSelectWrapper = document.querySelector('#rs-custom-select-address-state');
    const stateHiddenInput = stateSelectWrapper?.querySelector('input[type="hidden"]');
    let provinceCode = stateHiddenInput?.value?.trim() || '';
    if (!provinceCode) {
      const selectedStateOption = stateSelectWrapper?.querySelector('.rs-custom-select__option.is-selected');
      provinceCode = selectedStateOption?.getAttribute('data-value')?.trim() || '';
      if (provinceCode && stateHiddenInput) {
        stateHiddenInput.value = provinceCode;
      }
    }

    return { companyName, attention, city, address1, address2, zip, phone, countryCode, provinceCode };
  }

  /**
   * Call the address-validate endpoint to check the address with Google.
   * Returns the full Google Address Validation API response.
   */
  async validateAddressWithApi({ address1, address2, countryCode, city, provinceCode, zip }) {
    const response = await fetch(`${getCustomerAppBaseUrl()}/api/customer/address-validate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${window.myJWT || ''}`,
      },
      body: JSON.stringify({
        addressLine1: address1,
        addressLine2: address2 || '',
        countryCode: countryCode || 'US',
        city: city,
        stateCode: provinceCode,
        zip: zip,
      }),
    });

    if (!response.ok) {
      throw new Error(`Address validation failed with status ${response.status}`);
    }

    return response.json();
  }

  /**
   * Parse the Google Address Validation API response into our form shape.
   * @param {{ postalAddress?: { addressLines?: string[], locality?: string, administrativeArea?: string, postalCode?: string, regionCode?: string } }} addr
   * @returns {{ address1: string, address2: string, city: string, provinceCode: string, zip: string, countryCode: string }}
   */
  parseValidatedAddress(addr) {
    if (!addr) return { address1: '', address2: '', city: '', provinceCode: '', zip: '', countryCode: 'US' };
    const postal = addr.postalAddress || addr;
    const lines = postal.addressLines || [];
    const address1 = lines[0] || '';
    const address2 = lines[1] || '';
    let state = postal.administrativeArea || '';
    if (state && (postal.regionCode || 'US') === 'US' && state.length > 2) {
      state = US_STATE_NAME_TO_CODE[state] || state;
    }
    return {
      address1,
      address2,
      city: postal.locality || '',
      provinceCode: state,
      zip: postal.postalCode || '',
      countryCode: postal.regionCode || 'US',
    };
  }

  /**
   * Extract the full ZIP code (with +4 suffix when available) from the validation result.
   * Uses postalAddress.postalCode which already includes the suffix (e.g. "10011-1980"),
   * and falls back to building it from addressComponents if needed.
   * @param {{ address?: { postalAddress?: object, addressComponents?: object[] } }} result
   * @returns {string|null}
   */
  getValidatedZip(result) {
    const postal = result?.address?.postalAddress;
    if (postal?.postalCode) return postal.postalCode;

    // Fallback: build from addressComponents
    const components = result?.address?.addressComponents || [];
    const postalCode = components.find(c => c.componentType === 'postal_code')?.componentName?.text || '';
    const suffix = components.find(c => c.componentType === 'postal_code_suffix')?.componentName?.text || '';
    if (postalCode && suffix) return `${postalCode}-${suffix}`;
    return postalCode || null;
  }

  /**
   * Fill the address form with validated/suggested address values.
   */
  fillAddressFormWithSuggested(suggested) {
    if (this.addressFormAddress1Input && 'address1' in suggested) this.addressFormAddress1Input.value = suggested.address1 || '';
    if (this.addressFormAddress2Input && 'address2' in suggested) this.addressFormAddress2Input.value = suggested.address2 || '';
    if (this.addressFormCityInput) this.addressFormCityInput.value = suggested.city || '';
    if (this.addressFormZipInput) this.addressFormZipInput.value = suggested.zip || '';

    // Preserve the current state value in case the suggestion omits provinceCode.
    // Clicking a country option triggers syncAddressBookStateMenu which clears the state —
    // if Google's suggestion has no state we need to restore what the user had.
    const stateHiddenInput = document.querySelector('#rs-custom-select-address-state input[type="hidden"]');
    const savedProvinceCode = stateHiddenInput?.value || '';

    // Update country first — clicking a country option triggers syncAddressBookStateMenu
    // which rebuilds the state menu and clears the state value. Country must be set before
    // state so the menu rebuild doesn't wipe out the state we're about to set.
    if (suggested.countryCode) {
      const countrySelectWrapper = document.querySelector('#rs-custom-select-address-country');
      const countryMenu = countrySelectWrapper?.querySelector('.rs-custom-select__menu');
      if (countryMenu) {
        const countryOption = countryMenu.querySelector(`[data-value="${suggested.countryCode}"]`);
        if (countryOption) countryOption.click();
      }
    }

    // Determine which province code to use — prefer Google's suggestion, fall back to
    // what the user had before (savedProvinceCode) so the country click doesn't silently
    // clear a state the user already selected.
    const provinceCode = suggested.provinceCode || savedProvinceCode;
    if (provinceCode) {
      const iso = suggested.countryCode || 'US';
      this.syncAddressBookStateMenu(iso, { selectedValue: provinceCode });
    }
  }

  /**
   * Show a modal asking the user to confirm or edit a suggested address.
   * @param {string} formattedAddress - multi-line display string
   * @param {() => void} onUse - called when user clicks "Use this address"
   * @param {() => void} onEdit - called when user clicks "Edit address"
   */
  showAddressConfirmModal(formattedAddress, onUse, onEdit) {
    const existing = document.getElementById('rs-address-validation-modal');
    if (existing) existing.remove();

    const overlay = document.createElement('div');
    overlay.id = 'rs-address-validation-modal';
    overlay.className = 'rs-address-validation-modal-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'rs-address-validation-modal-title');
    overlay.innerHTML = `
      <div class="rs-address-validation-modal">
        <h2 id="rs-address-validation-modal-title" class="rs-address-validation-modal__title">Confirm address</h2>
        <p class="rs-address-validation-modal__subtitle">We found a close match for the address you entered. Would you like to use the suggested address?</p>
        <p class="rs-address-validation-modal__address">${this.escapeHtml(formattedAddress)}</p>
        <div class="rs-address-validation-modal__actions">
          <button type="button" class="rs-button rs-button-primary rs-button-medium rs-address-validation-modal__use-btn">Use this address</button>
          <button type="button" class="rs-button rs-button-secondary rs-button-medium rs-address-validation-modal__edit-btn">Edit address</button>
        </div>
      </div>
    `;

    const close = () => {
      overlay.remove();
      document.body.style.overflow = '';
      removeTrapFocus();
      if (this.addAddressModal && !this.addAddressModal.classList.contains('rs-hidden')) {
        this.setPageInertBehindModal(this.addAddressModal);
        this.scheduleModalFocusTrap(this.addAddressModal, {
          initialFocus:
            this.addressFormCompanyNameInput instanceof HTMLElement
              ? this.addressFormCompanyNameInput
              : null,
        });
      } else {
        this.clearPageInertBehindModal();
      }
    };

    overlay.querySelector('.rs-address-validation-modal__use-btn').addEventListener('click', () => {
      close();
      onUse();
    });
    overlay.querySelector('.rs-address-validation-modal__edit-btn').addEventListener('click', () => {
      close();
      onEdit();
    });
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        close();
        onEdit();
      }
    });

    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    this.clearPageInertBehindModal();
    this.setPageInertBehindModal(overlay);

    const useBtn = overlay.querySelector('.rs-address-validation-modal__use-btn');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        trapFocus(overlay, {
          initialFocus: useBtn instanceof HTMLElement ? useBtn : undefined,
        });
      });
    });
  }

  /**
   * Show an inline error on the Address Line 1 field (used for address validation failures).
   * @param {string} message
   */
  showAddressValidationError(message) {
    const input = this.addressFormAddress1Input;
    if (!input) return;
    const wrapper = input.closest('.rs-input__content');
    if (wrapper) {
      wrapper.classList.add('rs-error');
    }
    input.setAttribute('aria-invalid', 'true');
    // Show the existing error element or create a temporary one
    const errorEl = wrapper?.querySelector('.rs-input__error');
    if (errorEl) {
      errorEl.querySelector('span').textContent = message;
      errorEl.style.display = '';
    }
  }

  /**
   * Map Google Address Validation component types to our form field inputs.
   */
  getFieldForComponentType(componentType) {
    const mapping = {
      street_number: this.addressFormAddress1Input,
      route: this.addressFormAddress1Input,
      subpremise: this.addressFormAddress2Input,
      locality: this.addressFormCityInput,
      administrative_area_level_1: this.addressFormProvinceInput,
      postal_code: this.addressFormZipInput,
      postal_code_suffix: this.addressFormZipInput,
    };
    return mapping[componentType] || null;
  }

  /**
   * Normalize a US state/province value to a 2-letter code when possible.
   * @param {string} code
   * @param {string} [countryCode]
   * @returns {string}
   */
  normalizeProvinceCode(code, countryCode = 'US') {
    const trimmed = String(code || '').trim();
    if (!trimmed) return '';
    if ((countryCode || 'US').toUpperCase() !== 'US') return trimmed.toUpperCase();
    if (trimmed.length <= 2) return trimmed.toUpperCase();
    return US_STATE_NAME_TO_CODE[trimmed] || trimmed.toUpperCase();
  }

  /**
   * Compare the user's selected province with a Google address component or parsed code.
   * @param {string} userCode
   * @param {string} googleCode
   * @param {string} [countryCode]
   * @returns {boolean}
   */
  provinceCodesMatch(userCode, googleCode, countryCode = 'US') {
    const user = this.normalizeProvinceCode(userCode, countryCode);
    const google = this.normalizeProvinceCode(googleCode, countryCode);
    return Boolean(user && google && user === google);
  }

  /**
   * Hide the static Liquid-rendered required error for inputs and custom selects.
   * @param {HTMLElement | null | undefined} wrapper
   */
  suppressStaticFieldError(wrapper) {
    if (!wrapper) return;
    const staticError =
      wrapper.querySelector('.rs-input__error') || wrapper.querySelector('.rs-custom-select__error');
    if (staticError) staticError.style.display = 'none';
  }

  /**
   * Restore static required error visibility to CSS-controlled state.
   * @param {HTMLElement | null | undefined} wrapper
   */
  restoreStaticFieldError(wrapper) {
    if (!wrapper) return;
    const staticError =
      wrapper.querySelector('.rs-input__error') || wrapper.querySelector('.rs-custom-select__error');
    if (staticError) staticError.style.display = '';
  }

  /**
   * Map Google Address Validation component types to user-friendly field labels.
   */
  getLabelForComponentType(componentType) {
    const keyMap = {
      street_number: 'address_validation_label_street_number',
      route: 'address_validation_label_route',
      subpremise: 'address_validation_label_subpremise',
      locality: 'address_validation_label_locality',
      administrative_area_level_1: 'address_validation_label_state',
      postal_code: 'address_validation_label_postal_code',
      postal_code_suffix: 'address_validation_label_postal_code',
    };
    const key = keyMap[componentType];
    return key ? window.__t(key) : componentType;
  }

  /**
   * Show specific field-level errors based on unconfirmed or missing address components from Google.
   * Falls back to a generic error on Address Line 1 if no specific components are identified.
   * @param {{ addressComponents?: object[], missingComponentTypes?: string[], unconfirmedComponentTypes?: string[] }} address
   */
  showFixErrors(address) {
    const components = address?.addressComponents || [];
    const suspicious = components.filter(
      c => c.confirmationLevel === 'UNCONFIRMED_AND_SUSPICIOUS' || c.confirmationLevel === 'UNCONFIRMED_BUT_PLAUSIBLE'
    );
    const missingTypes = address?.missingComponentTypes || [];
    const { provinceCode, countryCode } = this.getAddressFormValues();
    const suggestedProvince = this.parseValidatedAddress(address).provinceCode;

    const erroredFields = new Set();

    const shouldSkipProvinceError = (googleCode) =>
      provinceCode &&
      (this.provinceCodesMatch(provinceCode, googleCode, countryCode) ||
        this.provinceCodesMatch(provinceCode, suggestedProvince, countryCode));

    const addFieldError = (field, message) => {
      if (!field || erroredFields.has(field)) return;
      erroredFields.add(field);
      this.showFieldValidationMessage(field, message, true);
    };

    for (const componentType of missingTypes) {
      if (componentType === 'administrative_area_level_1' && provinceCode) continue;

      const field = this.getFieldForComponentType(componentType);
      const message = field === this.addressFormAddress1Input
        ? window.__t('address_validation_address_unverified')
        : window.__t('address_validation_component_missing', {
            label: this.getLabelForComponentType(componentType),
          });
      addFieldError(field, message);
    }

    for (const component of suspicious) {
      if (component.componentType === 'administrative_area_level_1') {
        const googleCode = component.componentName?.text || suggestedProvince;
        if (shouldSkipProvinceError(googleCode)) continue;
      }

      const field = this.getFieldForComponentType(component.componentType);
      if (!field) continue;

      const label = this.getLabelForComponentType(component.componentType);
      const message = component.confirmationLevel === 'UNCONFIRMED_AND_SUSPICIOUS'
        ? window.__t('address_validation_component_unverified', { label })
        : window.__t('address_validation_component_unconfirmed', { label });

      addFieldError(field, message);
    }

    if (erroredFields.size === 0) {
      this.showAddressValidationError(window.__t('address_validation_fix_message'));
    }
  }

  async addAddress() {
    const customerId = document.body.getAttribute('data-customer-id');
    if (!customerId) {
      console.error('Customer ID not found');
      return;
    }

    const { companyName, attention, city, address1, address2, zip, phone, countryCode, provinceCode } = this.getAddressFormValues();
    const stateRequired = this.addressBookCountryHasStateOptions(countryCode);

    // Validate required fields
    if (!companyName || !address1 || !city || !zip || !phone || !countryCode || (stateRequired && !provinceCode)) {
      const fields = [
        this.addressFormCompanyNameInput,
        this.addressFormAddress1Input,
        this.addressFormCityInput,
      ];
      if (stateRequired) fields.push(this.addressFormProvinceInput);
      fields.push(this.addressFormZipInput, this.addressFormPhoneInput, this.addressFormCountryInput);
      this.errorValidation(fields);
      return;
    }

    // Validate zip and phone format before calling API
    if (!this.isValidZip(zip, countryCode)) {
      this.showFieldValidationMessage(this.addressFormZipInput, 'Enter a valid ZIP code.');
      return;
    }
    if (!this.isZipMatchingState(zip, provinceCode, countryCode)) {
      this.showFieldValidationMessage(this.addressFormZipInput, 'ZIP code does not match the selected state.');
      return;
    }
    if (!this.isValidPhone(phone)) {
      this.showFieldValidationMessage(this.addressFormPhoneInput, 'Enter a valid phone number.');
      return;
    }

    // Validate address with Google Address Validation API
    const addBtn = this.submitAddressFormButtonAdd;
    const addBtnDefaultText = addBtn?.textContent || 'Add Address';

    try {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.textContent = 'Validating address...';
      }

      const validationResponse = await this.validateAddressWithApi({ address1, address2, countryCode, city, provinceCode, zip });
      const result = validationResponse?.result;
      const verdict = result?.verdict;
      const nextAction = verdict?.possibleNextAction;

      if (nextAction === 'FIX') {
        this.showFixErrors(result?.address);
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = addBtnDefaultText;
        }
        return;
      }

      if (nextAction === 'CONFIRM_ADD_SUBPREMISES') {
        // Address Line 2 is optional — if the user didn't provide one, proceed with saving
        // rather than forcing them to enter a subpremise.
        if (!address2) {
          // Fall through to save
        } else if (this._subpremisesShown) {
          // User already acknowledged the subpremise warning on a previous validation —
          // Google still says the address is valid (just imprecise), so proceed to save.
          this._subpremisesShown = false;
          if (addBtn) addBtn.textContent = 'Saving...';
          // Fall through to save below
        } else {
          this._subpremisesShown = true;
          // Only update city/state/zip from Google's suggestion — do NOT overwrite address1/address2
          // since the user needs to keep their address1 and add a unit number in address2.
          const suggested = this.parseValidatedAddress(result?.address || {});
          const { address1: _a1, address2: _a2, ...suggestedWithoutAddressLines } = suggested;
          this.fillAddressFormWithSuggested(suggestedWithoutAddressLines);
          this.showFieldValidationMessage(
            this.addressFormAddress2Input,
            window.__t('address_validation_subpremise_needed'),
            true,
          );
          this.addressFormAddress2Input?.focus();
          if (addBtn) {
            addBtn.disabled = false;
            addBtn.textContent = addBtnDefaultText;
          }
          return;
        }
      }

      if (nextAction === 'CONFIRM') {
        const suggested = this.parseValidatedAddress(result?.address || {});
        const parts = [
          suggested.address1,
          suggested.address2,
          [suggested.city, suggested.provinceCode, suggested.zip].filter(Boolean).join(', '),
          suggested.countryCode,
        ].filter(Boolean);
        const formattedAddress = parts.join('\n');

        this.showAddressConfirmModal(
          formattedAddress,
          () => {
            // User accepted the suggested address — fill and re-submit
            this.fillAddressFormWithSuggested(suggested);
            if (addBtn) {
              addBtn.textContent = 'Saving...';
            }
            this.saveNewAddress(customerId, addBtn, addBtnDefaultText);
          },
          () => {
            // User chose to edit — re-enable button
            if (addBtn) {
              addBtn.disabled = false;
              addBtn.textContent = addBtnDefaultText;
            }
          },
        );
        return;
      }

      // ACCEPT or no verdict: update ZIP with validated value (includes +4 suffix) and proceed
      const validatedZip = this.getValidatedZip(result);
      if (validatedZip && this.addressFormZipInput) {
        this.addressFormZipInput.value = validatedZip;
      }

      if (addBtn) {
        addBtn.textContent = 'Saving...';
      }
    } catch (err) {
      console.error('Address validation error:', err);
      alert('Address validation failed. Please check the address.');
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.textContent = addBtnDefaultText;
      }
      return;
    }

    await this.saveNewAddress(customerId, addBtn, addBtnDefaultText);
  }

  /**
   * Save a new address after validation has passed or the user accepted a suggestion.
   * @param {string} customerId - The Shopify customer ID
   * @param {HTMLElement} addBtn - The submit button element
   * @param {string} addBtnDefaultText - Original button text to restore
   */
  async saveNewAddress(customerId, addBtn, addBtnDefaultText) {
    const { companyName, attention, city, address1, address2, zip, phone, countryCode, provinceCode } = this.getAddressFormValues();

    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/customer/address`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
        body: JSON.stringify({
          customer_id: 'gid://shopify/Customer/' + customerId,
          company: companyName,
          attention: attention || null,
          address1: address1,
          address2: address2 || null,
          city: city,
          state_code: provinceCode,
          zip: zip,
          phone: phone,
          country_code: countryCode,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData?.error || errorData?.details?.[0]?.message || 'Failed to add address. Please try again.';
        console.error('Failed to add address:', errorMessage);
        alert(errorMessage);
        if (addBtn) {
          addBtn.disabled = false;
          addBtn.textContent = addBtnDefaultText;
        }
        return;
      }

      const result = await response.json();

      // Get the new address from the response
      const newAddress = result.address || result;

      // Add the new address to the arrays
      this.allAddresses.push(newAddress);
      this.filteredAddresses.push(newAddress);

      // Render the new address item
      await this.fetchAndRenderAddresses(customerId);

      if (addBtn) {
        addBtn.disabled = false;
        addBtn.textContent = addBtnDefaultText;
      }

      this.closeAddAddressModalHandler();
      this.updateAddAddressButtonsState();
    } catch (error) {
      console.error('Error adding address:', error);
      alert('Error adding address. Please try again.');
      if (addBtn) {
        addBtn.disabled = false;
        addBtn.textContent = addBtnDefaultText;
      }
    }
  }

  async editAddress() {
    if (!this.currentEditingAddressId) {
      console.error('No address ID found for editing');
      return;
    }

    const customerId = document.body.getAttribute('data-customer-id');
    if (!customerId) {
      console.error('Customer ID not found');
      return;
    }

    const { companyName, attention, city, address1, address2, zip, phone, countryCode, provinceCode } = this.getAddressFormValues();
    const stateRequired = this.addressBookCountryHasStateOptions(countryCode);

    // Validate required fields
    if (!companyName || !address1 || !city || !zip || !phone || !countryCode || (stateRequired && !provinceCode)) {
      const fields = [
        this.addressFormCompanyNameInput,
        this.addressFormAddress1Input,
        this.addressFormCityInput,
      ];
      if (stateRequired) fields.push(this.addressFormProvinceInput);
      fields.push(this.addressFormZipInput, this.addressFormPhoneInput, this.addressFormCountryInput);
      this.errorValidation(fields);
      return;
    }

    // Validate zip and phone format before calling API
    if (!this.isValidZip(zip, countryCode)) {
      this.showFieldValidationMessage(this.addressFormZipInput, 'Enter a valid ZIP code.');
      return;
    }
    if (!this.isZipMatchingState(zip, provinceCode, countryCode)) {
      this.showFieldValidationMessage(this.addressFormZipInput, 'ZIP code does not match the selected state.');
      return;
    }
    if (!this.isValidPhone(phone)) {
      this.showFieldValidationMessage(this.addressFormPhoneInput, 'Enter a valid phone number.');
      return;
    }

    let addressGid = this.currentEditingAddressId;
    if (addressGid && !addressGid.startsWith('gid://')) {
      addressGid = `gid://shopify/CustomerAddress/${addressGid}`;
    }

    // Validate address with Google Address Validation API
    const saveBtn = this.submitAddressFormButtonEdit;
    const saveBtnDefaultText = saveBtn?.textContent || 'Save Address';

    try {
      if (saveBtn) {
        saveBtn.disabled = true;
        saveBtn.textContent = 'Validating address...';
      }

      const validationResponse = await this.validateAddressWithApi({ address1, address2, countryCode, city, provinceCode, zip });
      const result = validationResponse?.result;
      const verdict = result?.verdict;
      const nextAction = verdict?.possibleNextAction;

      if (nextAction === 'FIX') {
        this.showFixErrors(result?.address);
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.textContent = saveBtnDefaultText;
        }
        return;
      }

      if (nextAction === 'CONFIRM_ADD_SUBPREMISES') {
        // Address Line 2 is optional — if the user didn't provide one, proceed with saving
        // rather than forcing them to enter a subpremise.
        if (!address2) {
          // Fall through to save
        } else if (this._subpremisesShown) {
          // User already acknowledged the subpremise warning on a previous validation —
          // Google still says the address is valid (just imprecise), so proceed to save.
          this._subpremisesShown = false;
          if (saveBtn) saveBtn.textContent = 'Saving...';
          // Fall through to save below
        } else {
          this._subpremisesShown = true;
          // Only update city/state/zip from Google's suggestion — do NOT overwrite address1/address2
          // since the user needs to keep their address1 and add a unit number in address2.
          const suggested = this.parseValidatedAddress(result?.address || {});
          const { address1: _a1, address2: _a2, ...suggestedWithoutAddressLines } = suggested;
          this.fillAddressFormWithSuggested(suggestedWithoutAddressLines);
          this.showFieldValidationMessage(
            this.addressFormAddress2Input,
            window.__t('address_validation_subpremise_needed'),
            true,
          );
          this.addressFormAddress2Input?.focus();
          if (saveBtn) {
            saveBtn.disabled = false;
            saveBtn.textContent = saveBtnDefaultText;
          }
          return;
        }
      }

      if (nextAction === 'CONFIRM') {
        const suggested = this.parseValidatedAddress(result?.address || {});
        const parts = [
          suggested.address1,
          suggested.address2,
          [suggested.city, suggested.provinceCode, suggested.zip].filter(Boolean).join(', '),
          suggested.countryCode,
        ].filter(Boolean);
        const formattedAddress = parts.join('\n');

        this.showAddressConfirmModal(
          formattedAddress,
          () => {
            // User accepted the suggested address — fill and re-submit
            this.fillAddressFormWithSuggested(suggested);
            if (saveBtn) {
              saveBtn.textContent = 'Saving...';
            }
            this.saveEditedAddress(customerId, addressGid, saveBtn, saveBtnDefaultText);
          },
          () => {
            // User chose to edit — re-enable button
            if (saveBtn) {
              saveBtn.disabled = false;
              saveBtn.textContent = saveBtnDefaultText;
            }
          },
        );
        return;
      }

      // ACCEPT or no verdict: update ZIP with validated value (includes +4 suffix) and proceed
      const validatedZip = this.getValidatedZip(result);
      if (validatedZip && this.addressFormZipInput) {
        this.addressFormZipInput.value = validatedZip;
      }

      if (saveBtn) {
        saveBtn.textContent = 'Saving...';
      }
    } catch (err) {
      console.error('Address validation error:', err);
      alert('Address validation failed. Please check the address.');
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = saveBtnDefaultText;
      }
      return;
    }

    await this.saveEditedAddress(customerId, addressGid, saveBtn, saveBtnDefaultText);
  }

  /**
   * Save an edited address after validation has passed or the user accepted a suggestion.
   * @param {string} customerId - The Shopify customer ID
   * @param {string} addressGid - The Shopify address GID
   * @param {HTMLElement} saveBtn - The submit button element
   * @param {string} saveBtnDefaultText - Original button text to restore
   */
  async saveEditedAddress(customerId, addressGid, saveBtn, saveBtnDefaultText) {
    const { companyName, attention, city, address1, address2, zip, phone, countryCode, provinceCode } = this.getAddressFormValues();

    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/customer/address-update?customerId=gid://shopify/Customer/${customerId}&addressId=${encodeURIComponent(addressGid)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
        body: JSON.stringify({
          company: companyName,
          attention: attention || null,
          address1: address1,
          address2: address2 || null,
          city: city,
          stateCode: provinceCode,
          zip: zip,
          phone: phone,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update address:', errorData?.details[0]?.message || 'Failed to update address. Please try again.');
        alert(errorData?.details[0]?.message || 'Failed to update address. Please try again.');
        if (saveBtn) {
          saveBtn.disabled = false;
          saveBtn.textContent = saveBtnDefaultText;
        }
        return;
      }

      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = saveBtnDefaultText;
      }

      this.closeAddAddressModalHandler();

      // Refresh address list
      await this.fetchAndRenderAddresses(customerId);
    } catch (error) {
      console.error('Error updating address:', error);
      alert('Error updating address. Please try again.');
      if (saveBtn) {
        saveBtn.disabled = false;
        saveBtn.textContent = saveBtnDefaultText;
      }
    }
  }

  errorValidation(fields, type) {
    fields.forEach((field) => {
      if (!field) return;

      const wrapper = field.closest('.rs-input__content, .rs-custom-select__wrapper');
      const value = (field.value ?? '').trim();
      const isEmpty = value === '';

      if (isEmpty && type !== 'close') {
        wrapper?.classList.add('rs-error');
        field.setAttribute('aria-invalid', 'true');
      } else {
        wrapper?.classList.remove('rs-error');
        field.setAttribute('aria-invalid', 'false');
        this.restoreStaticFieldError(wrapper);
      }

      // Remove the error when the field is changed
      field.addEventListener('change', () => {
        const fieldWrapper = field.closest('.rs-input__content, .rs-custom-select__wrapper');
        fieldWrapper?.classList.remove('rs-error');
        field.setAttribute('aria-invalid', 'false');
        this.clearFieldValidationMessage(field);
      });
    });
  }

  /**
   * Initialize real-time input validation/filtering on address form fields.
   * Binds directly to known input element references to avoid reliance on
   * data attributes that may not render in Liquid snippet scope.
   */
  /**
   * Re-validate the ZIP code field when the state selection changes.
   */
  bindStateChangeZipRevalidation() {
    const stateHiddenInput = document.querySelector('#rs-custom-select-address-state input[type="hidden"]');
    if (!stateHiddenInput || !this.addressFormZipInput) return;

    stateHiddenInput.addEventListener('change', () => {
      const zip = this.addressFormZipInput.value.trim();
      if (!zip) return;

      const countrySelectWrapper = document.querySelector('#rs-custom-select-address-country');
      const countryCode = countrySelectWrapper?.querySelector('input[type="hidden"]')?.value || 'US';
      const stateCode = stateHiddenInput.value;

      // Clear any existing zip validation message first
      this.clearFieldValidationMessage(this.addressFormZipInput);

      if (!this.isValidZip(zip, countryCode)) {
        this.showFieldValidationMessage(this.addressFormZipInput, 'Enter a valid ZIP code.', true);
      } else if (!this.isZipMatchingState(zip, stateCode, countryCode)) {
        this.showFieldValidationMessage(this.addressFormZipInput, 'ZIP code does not match the selected state.', true);
      }
    });
  }

  initInputValidation() {
    const alphanumericFields = [
      this.addressFormCompanyNameInput,
      this.addressFormAttentionInput,
      this.addressFormAddress1Input,
    ];
    const alphaFields = [
      this.addressFormCityInput,
    ];
    const bindFilter = (input, regex, message) => {
      if (!input) return;
      input.addEventListener('input', () => {
        const cursorPos = input.selectionStart;
        const original = input.value;
        const filtered = original.replace(regex, '');
        if (filtered !== original) {
          input.value = filtered;
          const diff = original.length - filtered.length;
          input.setSelectionRange(cursorPos - diff, cursorPos - diff);
          this.showFieldValidationMessage(input, message, false);
        }
      });
    };

    alphanumericFields.forEach((input) => bindFilter(input, /[^a-zA-Z0-9 ]/g, 'Only letters, numbers, and spaces are allowed.'));
    alphaFields.forEach((input) => bindFilter(input, /[^a-zA-Z ]/g, 'Only letters and spaces are allowed.'));
    bindFilter(this.addressFormPhoneInput, /[^0-9+]/g, 'Only numbers and + are allowed.');

    if (this.addressFormZipInput) {
      this.addressFormZipInput.addEventListener('input', () => {
        const input = this.addressFormZipInput;
        const countryCode = (this.addressFormCountryInput?.value || 'US').toUpperCase();
        const regex = countryCode === 'CA' ? /[^a-zA-Z0-9 \-]/g : /[^0-9\-]/g;
        const message = countryCode === 'CA'
          ? 'Only letters, numbers, spaces, and dashes are allowed.'
          : 'Only numbers and dashes are allowed.';
        const cursorPos = input.selectionStart;
        const original = input.value;
        const filtered = original.replace(regex, '');
        if (filtered !== original) {
          input.value = filtered;
          const diff = original.length - filtered.length;
          input.setSelectionRange(cursorPos - diff, cursorPos - diff);
          this.showFieldValidationMessage(input, message, false);
        }
      });
    }
  }

  /**
   * Show a validation message below a field.
   * If the field is inside a row (City/State/ZIP), the message is placed after the row
   * as a full-width element so it isn't crammed into a narrow grid cell.
   * @param {HTMLElement} input
   * @param {string} message
   * @param {boolean} persistent - if true, message stays until clearFieldValidationMessage is called
   */
  showFieldValidationMessage(input, message, persistent = true) {
    // Resolve the wrapper — works for both regular inputs and custom selects
    const wrapper = input.closest('.rs-input__content') || input.closest('.rs-custom-select__wrapper');
    if (!wrapper) return;

    // Hide the static Liquid-rendered error (e.g. "State is required") so it doesn't stack
    this.suppressStaticFieldError(wrapper);

    // Mark the field as invalid visually
    wrapper.classList.add('rs-error');
    input.setAttribute('aria-invalid', 'true');

    // Determine where the message should be placed.
    // If the field is inside a form row (City/State/ZIP grid), place it after the row
    // so it renders full-width instead of being crammed into a narrow column.
    const row = input.closest('.rs-address-book-address-form__row');
    const messageContainer = row || wrapper;
    const messageId = input.id || input.name || '';
    const selector = row
      ? `.rs-input__validation-message[data-for="${messageId}"]`
      : '.rs-input__validation-message';

    let msgEl = messageContainer === row
      ? row.parentNode.querySelector(selector)
      : wrapper.querySelector(selector);

    if (!msgEl) {
      msgEl = document.createElement('div');
      msgEl.className = 'rs-input__validation-message rs-flex rs-align-center rs-gap-4 status-red text-small weight-500';
      msgEl.setAttribute('role', 'alert');
      if (row) {
        msgEl.setAttribute('data-for', messageId);
        // Insert after the row as a sibling in the form's flex layout
        row.insertAdjacentElement('afterend', msgEl);
      } else {
        wrapper.appendChild(msgEl);
      }
    }
    msgEl.innerHTML = this.escapeHtml(message);
    msgEl.style.display = '';

    clearTimeout(input._validationTimeout);
    if (!persistent) {
      input._validationTimeout = setTimeout(() => {
        this.clearFieldValidationMessage(input);
      }, 3000);
    }

    // For persistent messages, clear on the next user edit so stale errors don't linger
    if (persistent && !input._persistentClearBound) {
      input._persistentClearBound = true;
      const clearOnEdit = () => {
        this.clearFieldValidationMessage(input);
        input._persistentClearBound = false;
        input.removeEventListener('input', clearOnEdit);
        input.removeEventListener('change', clearOnEdit);
      };
      input.addEventListener('input', clearOnEdit);
      input.addEventListener('change', clearOnEdit);
    }
  }

  /**
   * Clear a dynamic validation message from a field.
   */
  clearFieldValidationMessage(input) {
    if (!input) return;
    clearTimeout(input._validationTimeout);
    const wrapper = input.closest('.rs-input__content') || input.closest('.rs-custom-select__wrapper');
    if (!wrapper) return;

    wrapper.classList.remove('rs-error');

    // Check for row-level message first (City/State/ZIP), then fall back to inline
    const row = input.closest('.rs-address-book-address-form__row');
    const messageId = input.id || input.name || '';
    let msgEl = null;

    if (row) {
      msgEl = row.parentNode.querySelector(`.rs-input__validation-message[data-for="${messageId}"]`);
    }
    if (!msgEl) {
      msgEl = wrapper.querySelector('.rs-input__validation-message');
    }

    if (msgEl) {
      msgEl.style.display = 'none';
    }
    this.restoreStaticFieldError(wrapper);
    input.setAttribute('aria-invalid', 'false');
  }

  /**
   * Clear all dynamic validation messages in the address form (both inline and row-level).
   * Called when the modal closes to ensure a clean state.
   */
  clearAllValidationMessages() {
    this._subpremisesShown = false;
    if (!this.addressForm) return;
    // Remove all inline and row-level validation messages
    this.addressForm.querySelectorAll('.rs-input__validation-message').forEach(el => {
      el.style.display = 'none';
    });
    // Also remove row-level messages that are siblings of the form rows
    this.addressForm.parentNode?.querySelectorAll('.rs-input__validation-message[data-for]').forEach(el => {
      el.style.display = 'none';
    });
    // Restore any hidden static errors
    this.addressForm.querySelectorAll('.rs-input__error').forEach(el => {
      el.style.display = '';
    });
    // Clear error styling
    this.addressForm.querySelectorAll('.rs-error').forEach(el => {
      el.classList.remove('rs-error');
    });
  }

  setupSearch() {
    // Try to find the search input - it might be loaded after DOM ready
    if (!this.searchInput) {
      // Wait a bit for the input to be rendered
      setTimeout(() => {
        this.searchInput = document.querySelector('.rs-address-book--search .rs-input');
        if (this.searchInput) {
          this.attachSearchListeners();
        }
      }, 100);
    } else {
      this.attachSearchListeners();
    }
  }

  /**
   * Sync disabled state on all "Add Address" buttons with the 25-address limit.
   * @param {number} [addressCount]
   */
  updateAddAddressButtonsState(addressCount = this.allAddresses.length) {
    const atLimit = addressCount >= 25;
    document.querySelectorAll('.rs-add-address').forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = atLimit;
      }
    });
  }

  setupAddAddressButton() {
    if (!this.addAddressButtons) return;
    this.addAddressButtons.forEach((button) => {
      button.addEventListener('click', () => this.openAddAddressModal('new'));
    });
  }

  /**
   * @param {string} type
   */
  openAddAddressModal(type, button) {
    if (!this.addAddressButtons || !this.addAddressModal) return;

    // Store the element that had focus before opening the modal
    this.previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.addAddressModal.classList.remove('rs-modal-title--new', 'rs-modal-title--edit');

    if (type === 'new') {
      this.addAddressModal.classList.add('rs-modal-title--new');
    } else if (type === 'edit') {
      this.populateModalWithAddressData(button);
      this.addAddressModal.classList.add('rs-modal-title--edit');
    }

    this.addAddressModal.classList.remove('rs-hidden');
    this.modalMask?.classList.remove('rs-hidden');

    // Update aria-hidden for accessibility
    this.addAddressModal.setAttribute('aria-hidden', 'false');
    this.modalMask?.setAttribute('aria-hidden', 'false');

    this.activateModalFocusTrap(this.addAddressModal, {
      initialFocus:
        this.addressFormCompanyNameInput instanceof HTMLElement
          ? this.addressFormCompanyNameInput
          : null,
    });
  }

  populateModalWithAddressData(button) {
    if (!button) return;
    const addressJson = button.closest('.rs-address-book-list__item')?.getAttribute('data-address-json');
    if (!addressJson) return;
    const address = JSON.parse(addressJson);

    // Store the address ID for editing
    this.currentEditingAddressId = address.id || button.getAttribute('data-address-id');

    if (this.addressFormCompanyNameInput) {
      this.addressFormCompanyNameInput.value = address.company || '';
    }
    if (this.addressFormAttentionInput) {
      this.addressFormAttentionInput.value = address.attention || '';
    }
    if (this.addressFormCountryInput) {
      const countrySelect = this.addressFormCountryInput.closest('.rs-custom-select').querySelector('.rs-custom-select__menu');
      const iso = address.countryCodeV2 || 'US';
      if (countrySelect) {
        countrySelect.querySelector(`[data-value="${iso}"]`)?.click();
      }
      const pc = (address.provinceCode || '').trim();
      this.syncAddressBookStateMenu(iso, { selectedValue: pc || undefined });
    }
    if (this.addressFormCityInput) {
      this.addressFormCityInput.value = address.city || '';
    }
    if (this.addressFormAddress1Input) {
      this.addressFormAddress1Input.value = address.address1 || '';
    }
    if (this.addressFormAddress2Input) {
      this.addressFormAddress2Input.value = address.address2 || '';
    }
    if (this.addressFormZipInput) {
      this.addressFormZipInput.value = address.zip || '';
    }
    if (this.addressFormPhoneInput) {
      this.addressFormPhoneInput.value = address.phone || '';
    }

    this.sanitizeFormValues();
  }

  /**
   * Sanitize all address form field values by stripping disallowed characters.
   * Called after populating the form with existing data to clean any legacy values.
   * Shows persistent validation messages that clear only when the user fixes the value.
   */
  sanitizeFormValues() {
    const countryCode = (this.addressFormCountryInput?.value || 'US').toUpperCase();
    const zipRegex = countryCode === 'CA' ? /[^a-zA-Z0-9 \-]/g : /[^0-9\-]/g;
    const zipMsg = countryCode === 'CA'
      ? 'Only letters, numbers, spaces, and dashes are allowed.'
      : 'Only numbers and dashes are allowed.';
    const fields = [
      { input: this.addressFormCompanyNameInput, regex: /[^a-zA-Z0-9 ]/g, msg: 'Only letters, numbers, and spaces are allowed.' },
      { input: this.addressFormAttentionInput,   regex: /[^a-zA-Z0-9 ]/g, msg: 'Only letters, numbers, and spaces are allowed.' },
      { input: this.addressFormAddress1Input,    regex: /[^a-zA-Z0-9 ]/g, msg: 'Only letters, numbers, and spaces are allowed.' },
      { input: this.addressFormCityInput,        regex: /[^a-zA-Z ]/g,    msg: 'Only letters and spaces are allowed.' },
      { input: this.addressFormZipInput,         regex: zipRegex,         msg: zipMsg },
      { input: this.addressFormPhoneInput,       regex: /[^0-9+]/g,      msg: 'Only numbers and + are allowed.' },
    ];

    fields.forEach(({ input, regex, msg }) => {
      if (!input || !input.value) return;
      const original = input.value;
      const filtered = original.replace(regex, '');
      if (filtered !== original) {
        input.value = filtered;
        this.showFieldValidationMessage(input, msg, true);

        // Clear the persistent message once the user edits and the value passes validation
        const onInput = () => {
          if (!regex.test(input.value)) {
            this.clearFieldValidationMessage(input);
            input.removeEventListener('input', onInput);
          }
        };
        input.addEventListener('input', onInput);
      }
    });
  }

  resetAddressForm() {
    if (this.addressFormCompanyNameInput) {
      this.addressFormCompanyNameInput.value = '';
    }
    if (this.addressFormAttentionInput) {
      this.addressFormAttentionInput.value = '';
    }
    if (this.addressFormCityInput) {
      this.addressFormCityInput.value = '';
    }
    if (this.addressFormAddress1Input) {
      this.addressFormAddress1Input.value = '';
    }
    if (this.addressFormAddress2Input) {
      this.addressFormAddress2Input.value = '';
    }
    if (this.addressFormZipInput) {
      this.addressFormZipInput.value = '';
    }
    if (this.addressFormPhoneInput) {
      this.addressFormPhoneInput.value = '';
    }

    // Reset custom selects
    const countrySelectWrapper = document.querySelector('#rs-custom-select-address-country');
    const countryHiddenInput = countrySelectWrapper?.querySelector('input[type="hidden"]');
    const countryButton = countrySelectWrapper?.querySelector('.rs-custom-select__button');
    const countryValueDisplay = countrySelectWrapper?.querySelector('.rs-custom-select__value');
    if (countryHiddenInput && countryButton && countryValueDisplay) {
      countryHiddenInput.value = 'US';
      countryValueDisplay.textContent = 'United States Of America';
    }

    this.syncAddressBookStateMenu('US');

    this.currentEditingAddressId = null;
  }

  attachSearchListeners() {
    if (!this.searchInput) return;

    // Debounce function to limit filtering operations
    let debounceTimer;
    const debounceDelay = 300;

    this.searchInput.addEventListener('input', (e) => {
      clearTimeout(debounceTimer);
      const searchTerm = e.target.value.trim().toLowerCase();

      debounceTimer = setTimeout(() => {
        this.filterAddresses(searchTerm);
      }, debounceDelay);
    });

    // Handle clear/reset button if it exists
    const searchWrapper = this.searchInput.closest('.rs-address-book--search');
    const clearButton = searchWrapper?.querySelector('.rs-icon__input-right, [data-action="clear"]');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.searchInput.value = '';
        this.filterAddresses('');
      });
    }
  }

  filterAddresses(searchTerm) {
    if (!searchTerm) {
      this.filteredAddresses = [...this.allAddresses];
    } else {
      this.filteredAddresses = this.allAddresses.filter((address) => {
        // Search in company name
        const company = (address.company || address.name || '').toLowerCase();

        // Search in address fields
        const address1 = (address.address1 || '').toLowerCase();
        const address2 = (address.address2 || '').toLowerCase();
        const city = (address.city || '').toLowerCase();
        const province = (address.province || '').toLowerCase();
        const zip = (address.zip || '').toLowerCase();
        const country = (address.country || '').toLowerCase();

        // Search in phone
        const phone = (address.phone || '').toLowerCase();

        // Search in location ID if it exists
        const locationId = (address.location_id || address.id || '').toLowerCase();

        // Combine all searchable fields
        const searchableText = `${company} ${address1} ${address2} ${city} ${province} ${zip} ${country} ${phone} ${locationId}`;

        return searchableText.includes(searchTerm);
      });
    }

    this.renderAddresses(this.filteredAddresses);
  }

  waitForDependencies() {
    return new Promise((resolve) => {
      const checkDependencies = () => {
        if (window.myJWT) {
          resolve();
        } else {
          setTimeout(checkDependencies, 100);
        }
      };
      checkDependencies();
    });
  }

  async fetchAndRenderAddresses(customerId) {
    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/customer/addresses?customer_id=gid://shopify/Customer/${customerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
      });

      const data = await response.json();
      const addresses = data.addresses || data || [];

      // Store all addresses for filtering
      if (response.ok) {
        this.allAddresses = addresses;
        this.filteredAddresses = [...addresses];
      } else {
        this.allAddresses = [];
        this.filteredAddresses = [];
      }

      this.renderAddresses(this.filteredAddresses);

      this.updateAddAddressButtonsState(addresses.length);

      // Init edit address functionality
      this.initEditAddressFunctionality();
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  }

  initEditAddressFunctionality() {
    if (!this.addAddressModal) return;
    const editAddressButtons = document.querySelectorAll('.rs-address-book-list__action-link[data-action="edit"]');
    editAddressButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        // Populate modal with address data

        this.openAddAddressModal('edit', button);
      });
    });
  }

  renderAddresses(addresses) {
    if (!this.container) return;

    // Clear the container
    this.container.innerHTML = '';

    if (addresses.length === 0) {
      this.section?.classList.add('rs-address-book--empty');
      // Show the "no addresses" card with Add Address button
      const emptyStateHTML = `
        <div class="rs-address-book-list__empty-state">
          <p class="rs-address-book-list__empty-message">You don't have any addresses yet</p>
          <button class="rs-button rs-button-primary text-small rs-button-small weight-600 rs-address-book-list__empty-add-button rs-add-address" type="button">
            Add Address
          </button>
        </div>
      `;
      this.container.innerHTML = emptyStateHTML;

      // Attach event listener to the Add Address button in empty state
      const emptyAddButton = this.container.querySelector('.rs-address-book-list__empty-add-button');
      if (emptyAddButton) {
        emptyAddButton.addEventListener('click', () => {
          this.openAddAddressModal('new');
        });
      }

      // Update address count
      const countElement = document.querySelector('.rs-address-book--count');
      if (countElement) {
        countElement.textContent = `(0)`;
      }

      // Update paragraph text
      const paragraphText = document.querySelector('.rs-address-book-text');
      if (paragraphText) {
        paragraphText.textContent = 'You can have up to 25 addresses in your address book.';
      }
      return;
    } else {
      this.section?.classList.remove('rs-address-book--empty');

      // Update paragraph text
      const paragraphText = document.querySelector('.rs-address-book-text');
      if (paragraphText) {
        paragraphText.textContent = 'You can have up to 25 addresses in your address book. The address for the currently-selected location appears automatically at checkout and does not need to be in your address book.';
      }
    }

    // Reverse addresses array to make sure newest addresses are at the top
    const reversedAddresses = [...addresses].reverse();

    this.container.innerHTML = reversedAddresses
      .map(
        (address) => `
      <div class="rs-address-book-list__item" data-address-json='${JSON.stringify(address)}'>
        <div class="rs-address-book-list__header">
          <h3 class="rs-address-book-list__company-name">${this.escapeHtml(address.company || address.name || '')}</h3>
          <div class="rs-address-book-list__actions rs-mob-hidden">
            <a href="#" class="rs-address-book-list__action-link" data-action="delete" data-address-id="${address.id || ''}">Delete</a>
            <a href="#" class="rs-address-book-list__action-link" data-action="edit" data-address-id="${address.id || ''}">Edit</a>
          </div>
        </div>
        ${address.attention ? `<p class="rs-address-book-list__attention">Attention: ${this.escapeHtml(address.attention)}</p>` : ''}
        <p class="rs-address-book-list__address">
          ${this.formatAddress(address)}
        </p>
        ${address.phone ? `<p class="rs-address-book-list__phone">${this.escapeHtml(address.phone)}</p>` : ''}
        <div class="rs-address-book-list__actions rs-desktop-hidden">
          <a href="#" class="rs-address-book-list__action-link" data-action="delete" data-address-id="${address.id || ''}">Delete</a>
          <a href="#" class="rs-address-book-list__action-link" data-action="edit" data-address-id="${address.id || ''}">Edit</a>
        </div>
      </div>
    `,
      )
      .join('');

    // Add event listeners for edit and delete actions
    this.attachEventListeners();

    // Update the rs-address-book--count element with the number of addresses
    const countElement = document.querySelector('.rs-address-book--count');
    if (countElement) {
      countElement.textContent = `(${addresses.length})`;
    }
  }

  formatAddress(address) {
    const parts = [];

    if (address.address1) parts.push(address.address1);
    if (address.address2) parts.push(address.address2);
    if (address.city) parts.push(address.city);
    if (address.provinceCode) parts.push(address.provinceCode);
    if (address.zip) parts.push(address.zip);
    if (address.country) parts.push(address.country);

    return this.escapeHtml(parts.join(', '));
  }

  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  getErrorIconSvg() {
    return '<svg class="rs-icon rs-icon-error" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 48 48"><path d="M48.0001 48.2632H6.10352e-05V0.263184H48.0001V48.2632ZM24.1407 32.5972C23.2704 32.5972 22.5874 32.8512 22.0919 33.3599C21.6097 33.8554 21.3682 34.4719 21.3682 35.2085C21.3682 35.9183 21.6097 36.5277 22.0919 37.0366C22.5874 37.5321 23.2703 37.7798 24.1407 37.7798C24.9978 37.7798 25.6744 37.5322 26.17 37.0366C26.6655 36.5277 26.9131 35.9183 26.9131 35.2085C26.9131 34.4719 26.6655 33.8554 26.17 33.3599C25.6745 32.851 24.9978 32.5972 24.1407 32.5972ZM21.5089 8.24854L22.0118 28.8804H26.1495L26.6319 8.24854H21.5089Z" fill="currentColor"/></svg>';
  }

  attachEventListeners() {
    const actionLinks = document.querySelectorAll('.rs-address-book-list__action-link');
    actionLinks.forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const action = link.getAttribute('data-action');
        const addressId = link.getAttribute('data-address-id');

        if (action === 'edit') {
          this.handleEdit(addressId);
        } else if (action === 'delete') {
          this.handleDelete(addressId);
        }
      });
    });
  }

  handleEdit(addressId) {
    // Find the edit button to get the address data
    const editButton = document.querySelector(`.rs-address-book-list__action-link[data-action="edit"][data-address-id="${addressId}"]`);
    if (editButton) {
      this.openAddAddressModal('edit', editButton);
    }
  }

  handleDelete(addressId) {
    // Find the delete button to get the address data
    const deleteButton = document.querySelector(`.rs-address-book-list__action-link[data-action="delete"][data-address-id="${addressId}"]`);
    if (deleteButton) {
      this.openDeleteModal(deleteButton);
    }
  }

  setupDeleteModal() {
    if (!this.deleteAddressModal) return;

    // Close modal handlers
    this.closeDeleteModalButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.closeDeleteModal();
      });
    });

    this.deleteModalMask?.addEventListener('click', () => {
      this.closeDeleteModal();
    });

    // Confirm delete handler
    this.deleteModalConfirmButton?.addEventListener('click', () => {
      this.confirmDelete();
    });
  }

  closeDeleteModalHandler() {
    this.releaseModalFocusTrap();

    this.deleteAddressModal.classList.add('rs-hidden');
    this.deleteModalMask?.classList.add('rs-hidden');
    this.deleteAddressModal.setAttribute('aria-hidden', 'true');
    this.deleteModalMask?.setAttribute('aria-hidden', 'true');
    this.currentDeletingAddressId = null;

    // Return focus to the element that opened the modal
    if (this.previousActiveElement && this.previousActiveElement instanceof HTMLElement) {
      this.previousActiveElement.focus();
      this.previousActiveElement = null;
    }
  }

  openDeleteModal(button) {
    if (!this.deleteAddressModal || !button) return;

    // Store the element that had focus before opening the modal
    this.previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const addressJson = button.closest('.rs-address-book-list__item')?.getAttribute('data-address-json');
    if (!addressJson) return;

    const address = JSON.parse(addressJson);
    this.currentDeletingAddressId = address.id || button.getAttribute('data-address-id');

    // Populate modal with address data
    if (this.deleteModalCompanyName) {
      this.deleteModalCompanyName.textContent = address.company || address.name || '';
    }

    if (this.deleteModalAddressLine) {
      const addressParts = [];
      if (address.address1) addressParts.push(address.address1);
      if (address.address2) addressParts.push(address.address2);
      if (address.city) addressParts.push(address.city);
      if (address.province) addressParts.push(address.province);
      if (address.zip) addressParts.push(address.zip);
      if (address.country) addressParts.push(address.country);
      this.deleteModalAddressLine.textContent = addressParts.join(', ');
    }

    if (this.deleteModalPhone) {
      this.deleteModalPhone.textContent = address.phone || '';
      this.deleteModalPhone.style.display = address.phone ? '' : 'none';
    }

    // Show modal
    this.deleteAddressModal.classList.remove('rs-hidden');
    this.deleteModalMask?.classList.remove('rs-hidden');

    // Update aria-hidden for accessibility
    this.deleteAddressModal.setAttribute('aria-hidden', 'false');
    this.deleteModalMask?.setAttribute('aria-hidden', 'false');

    const initialFocus =
      this.closeDeleteModalButtons[0] instanceof HTMLElement
        ? this.closeDeleteModalButtons[0]
        : this.deleteModalConfirmButton instanceof HTMLElement
          ? this.deleteModalConfirmButton
          : null;

    this.activateModalFocusTrap(this.deleteAddressModal, {initialFocus});
  }

  closeDeleteModal() {
    if (!this.deleteAddressModal) return;
    this.closeDeleteModalHandler();
  }

  async confirmDelete() {
    if (!this.currentDeletingAddressId) {
      console.error('No address ID found for deletion');
      return;
    }

    const customerId = document.body.getAttribute('data-customer-id');
    if (!customerId) {
      console.error('Customer ID not found');
      return;
    }

    let addressGid = this.currentDeletingAddressId;
    if (addressGid && !addressGid.startsWith('gid://')) {
      addressGid = `gid://shopify/CustomerAddress/${addressGid}`;
    }

    try {
      const response = await fetch(`${getCustomerAppBaseUrl()}/api/customer/address-delete?customerId=gid://shopify/Customer/${customerId}&addressId=${encodeURIComponent(addressGid)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${window.myJWT}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to delete address:', errorData);
        alert('Failed to delete address. Please try again.');
        return;
      }

      const result = await response.json();
      console.log('Address deleted successfully:', result);

      this.closeDeleteModal();

      // Refresh address list
      await this.fetchAndRenderAddresses(customerId);
    } catch (error) {
      console.error('Error deleting address:', error);
      alert('Error deleting address. Please try again.');
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AddressBookList();
  });
} else {
  new AddressBookList();
}
